import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, AlertTriangle } from "lucide-react";
import { getPortalItinerary } from "@/api/client";
import type { PortalOutletContext } from "@/pages/artist-portal/ArtistPortalDetailPage";
import { ScheduleEventDialog } from "../components/ScheduleEventDialog";

const SOURCE_DOT: Record<string, string> = {
  derived_slot: "bg-stage-stone",
  derived_hotel: "bg-warn",
  derived_transport: "bg-accent",
  schedule_event: "bg-ok",
};

export function ItineraryTab() {
  const { festivalId, overview } = useOutletContext<PortalOutletContext>();
  const tz = overview.festival.timezone;
  const q = useQuery({
    queryKey: ["artist-portal", festivalId, "itinerary"],
    queryFn: () => getPortalItinerary(festivalId),
  });
  const [showDialog, setShowDialog] = useState(false);

  if (q.isLoading) return <div className="text-ink-3">Loading itinerary…</div>;
  if (q.isError) return <div className="text-bad">Could not load itinerary.</div>;

  const days = q.data ?? [];
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz,
    });

  return (
    <div className="space-y-4">
      <div className="flex justify-stretch sm:justify-end">
        <button
          className="btn btn--accent w-full sm:w-auto"
          onClick={() => setShowDialog(true)}
        >
          <Plus className="h-4 w-4" /> Add event
        </button>
      </div>

      {days.length === 0 ? (
        <div className="card p-6 text-center text-sm text-ink-3 sm:p-8">
          No events yet. Add soundchecks, press calls, and other items to build your itinerary.
        </div>
      ) : (
        days.map((d) => (
          <section key={d.day} className="min-w-0">
            <h2 className="mb-2 flex min-w-0 flex-col gap-0.5 text-sm font-semibold uppercase tracking-wide text-ink-3 sm:flex-row sm:items-baseline sm:gap-2">
              {d.day}
              <span className="normal-case tracking-normal text-[10px] font-normal text-ink-3">
                times in {tz.replace("_", " ")}
              </span>
            </h2>
            <ol className="space-y-2 border-l-2 border-line-2 pl-4">
              {d.events.map((e) => (
                <li
                  key={`${e.source}-${e.start}-${e.title}`}
                  className="relative"
                >
                  <span
                    className={`absolute -left-[22px] top-3 block h-3 w-3 rounded-full ${
                      SOURCE_DOT[e.source] ?? "bg-ink-4"
                    }`}
                  />
                  <div className="card p-3">
                    <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <div className="break-words font-semibold text-ink">{e.title}</div>
                        <div className="break-words text-xs leading-relaxed text-ink-3">
                          {fmt(e.start)} → {fmt(e.end)}
                          {e.location ? ` · ${e.location}` : ""}
                        </div>
                      </div>
                      {e.overlap_with && e.overlap_with.length > 0 && (
                        <span className="inline-flex w-fit shrink-0 items-center gap-1 rounded-full bg-warn/10 px-2 py-0.5 text-[10px] text-warn">
                          <AlertTriangle className="h-3 w-3" /> overlaps
                        </span>
                      )}
                    </div>
                    {e.notes && (
                      <div className="mt-1 break-words text-sm leading-relaxed text-ink-2">{e.notes}</div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        ))
      )}

      {showDialog && (
        <ScheduleEventDialog
          festivalId={festivalId}
          festivalTimezone={tz}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
}
